import { Component, Input, ChangeDetectionStrategy } from '@angular/core';

export type BadgeStatus =
  | 'COMPLETED' | 'SIGNED'
  | 'IN_PROGRESS' | 'INPROGRESS'
  | 'INITIATED'
  | 'PENDING' | 'WAITFORAPROVE'
  | 'REJECTED' | 'FAILED'
  | string;

/**
 * Generic status pill. Colour is driven by --status-* CSS tokens
 * so it auto-adapts to every tenant theme without component changes.
 *
 * Usage:
 *   <app-status-badge status="COMPLETED" label="Completed"></app-status-badge>
 *   <app-status-badge [status]="order.status"></app-status-badge>
 */
@Component({
  selector: 'app-status-badge',
  template: `<span class="status-badge" [ngClass]="cssClass">{{ displayLabel }}</span>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class StatusBadgeComponent {
  @Input() status: BadgeStatus = '';
  @Input() label: string = '';

  private static readonly STATUS_MAP: Record<string, string> = {
    // ── Completed / success ────────────────────────────────────
    COMPLETED:                'completed',
    SIGNED:                   'completed',
    ESIGNED:                  'completed',
    SUCCESS:                  'completed',
    SUCCESSFUL:               'completed',
    PAYMENT_SUCCESSFUL:       'completed',
    PAYMENT_COMPLETED:        'completed',

    // ── In-progress / active ───────────────────────────────────
    IN_PROGRESS:              'in-progress',
    INPROGRESS:               'in-progress',
    'IN PROGRESS':            'in-progress',
    PROCESSING:               'in-progress',
    UPLOADED:                 'in-progress',
    PAYMENT_PROCESSING:       'in-progress',

    // ── Initiated / started ────────────────────────────────────
    INITIATED:                'initiated',
    DRAFT:                    'initiated',
    NEW:                      'initiated',

    // ── Pending / waiting ──────────────────────────────────────
    PENDING:                      'waitForAprove',
    WAITFORAPROVE:                'waitForAprove',
    'WATING FOR PAYMENT':         'waitForAprove',
    'WATING FOR STAMP':           'waitForAprove',
    'WAITING FOR PAYMENT':        'waitForAprove',
    WAITING_FOR_WALLET_PAYMENT:   'waitForAprove',
    WAITING_FOR_PAYMENT:          'waitForAprove',
    WAITING_FOR_STAMP:            'waitForAprove',
    'APPROVAL PENDING':           'waitForAprove',
    APPROVAL_PENDING:             'waitForAprove',
    WAITING_FOR_APPROVAL:         'waitForAprove',
    AWAITING_APPROVAL:            'waitForAprove',

    // ── Rejected / failed ──────────────────────────────────────
    REJECTED:              'rejected',
    FAILED:                'rejected',
    CANCELLED:             'rejected',
    CANCELED:              'rejected',
    EXPIRED:               'rejected',
    ERROR:                 'rejected',
    SERVICE_NOT_AVAILABLE: 'rejected',
    PAYMENT_FAILED:        'rejected',
    PAYMENT_PENDING:       'waitForAprove',

    // plural / alternate API spellings for stamp statuses
    'WATING FOR STAMPS':          'waitForAprove',
    'WAITING FOR STAMPS':         'waitForAprove',
    WAITING_FOR_STAMPS:           'waitForAprove',
  };

  get cssClass(): string {
    if (!this.status) return '';
    const s = this.status.trim().replace(/\s+/g, ' ').toUpperCase();

    // 1. Exact match
    const exact = StatusBadgeComponent.STATUS_MAP[s];
    if (exact) return exact;

    // 2. Try with underscores converted to spaces (handles raw API codes)
    const spaced = s.replace(/_/g, ' ');
    const fromUnderscore = StatusBadgeComponent.STATUS_MAP[spaced];
    if (fromUnderscore) return fromUnderscore;

    // 3. Keyword fallback for any unrecognised variant
    if (s.includes('COMPLET') || s.includes('SIGNED') || s.includes('SUCCESS')) return 'completed';
    if (s.includes('REJECT') || s.includes('FAIL') || s.includes('CANCEL') || s.includes('EXPIRED') || s.includes('ERROR')) return 'rejected';
    if (s.includes('WAITING') || s.includes('WATING') || s.includes('PENDING') || s.includes('APPROVAL') || s.includes('AWAITING') || s.includes('STAMP')) return 'waitForAprove';
    if (s.includes('PROGRESS') || s.includes('PROCESSING') || s.includes('UPLOADED')) return 'in-progress';
    if (s.includes('INITIAT') || s.includes('DRAFT')) return 'initiated';

    return '';
  }

  get displayLabel(): string {
    return this.label || this.toReadable(this.status);
  }

  private toReadable(s: string): string {
    if (!s) return '';
    return s
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, c => c.toUpperCase());
  }
}
